/**
 * video-index.js — /car-usb-video. Видео, которое откроет экран в машине.
 *
 * Родня музыкальной половины рубрики и тот же порядок рассуждения:
 *   1. спросить у файла, что с ним не так ДЛЯ ЭТОГО устройства;
 *   2. не трогать то, что и так играет;
 *   3. перекодировать только неиграбельное — и проверить, что вышло то, что
 *      просили.
 *
 * ── Что здесь принципиально сложнее, чем у музыки ──────────────────────────
 * У аудио «плохо» видно по заголовку и лечится побайтово. У видео главный
 * виновник невидим и не лечится ничем, кроме перекодирования: ПРОФИЛЬ H.264.
 * Телефон снимает в High, бюджетный экран декодирует только Constrained
 * Baseline, и файл при этом выглядит совершенно обычным mp4 с кодеком H.264.
 *
 * И второе: попросить у энкодера Baseline мало — надо ПРОВЕРИТЬ, что он его
 * дал. Аппаратные энкодеры кодек-строку принимают как подсказку и спокойно
 * отдают High. Поэтому выдача разбирается заново, и если профиль оказался
 * выше запрошенного, файл переделывается на FFmpeg с явным `-profile:v`.
 * Обещание, которое некому проверить, — это не обещание.
 */

import { analyzeVideo, PROFILE_RANK } from './video-analyze.js';
import { decideVideo, pickBitrate, VIDEO_PROFILES, FAT32_MAX_FILE } from './video-verdict.js';
import { buildPlan, sortItems } from './plan.js';

const SLUG = 'car-usb-video';
const VIDEO_EXT = ['mp4', 'm4v', 'mov', 'mkv', 'webm', 'avi', 'wmv', 'asf', 'flv', 'ts', 'mpg', 'mpeg', 'vob', '3gp', 'ogv', 'divx'];
const SUB_EXT = ['srt', 'vtt', 'ass', 'ssa', 'sub'];

let ui = null;
let items = [];
let seq = 0;
let running = false;

const settings = {
    profile: 'common',
    screen: 'auto',
    mode: 'ascii',
    order: 'as-is',
    numbering: true,
    folders: 'flat',
    quality: 'balanced',
};

const $ = (id) => document.getElementById(id);
const t = (k, sub) => {
    let s = (ui && ui.lang && ui.lang[k]) || k;
    if (sub) for (const p in sub) s = s.split(':' + p).join(sub[p]);
    return s;
};
/* Воронка. `__stage` сам считает время на шаге и досылает `final_stage`,
   когда человек уходит со страницы, — то есть отвечает на вопрос «где
   отваливаются», а не «сколько нажали». Слаг берётся из <meta name="tool-slug">,
   передавать его не надо. */
const stage = (n, d) => { try { window.__stage && window.__stage(n, d || {}); } catch (e) {} };
const ae = (ev, d) => { try { window.__ae && window.__ae(ev, Object.assign({ slug: SLUG }, d || {})); } catch (e) {} };
const extOf = (n) => { const i = String(n || '').lastIndexOf('.'); return i > 0 ? n.slice(i + 1).toLowerCase() : ''; };
const fmtSize = (b) => b >= 1073741824 ? (b / 1073741824).toFixed(2) + ' GB' : (b / 1048576).toFixed(0) + ' MB';
const fmtTime = (s) => {
    if (!isFinite(s) || s <= 0) return '';
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), ss = Math.round(s % 60);
    return (h ? h + ':' + String(m).padStart(2, '0') : String(m)) + ':' + String(ss).padStart(2, '0');
};
const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

/* ───────────────────────────── целевой экран ───────────────────────────── */

function screenSize() {
    if (settings.screen && settings.screen !== 'auto') {
        const [w, h] = settings.screen.split('x').map(Number);
        if (w && h) return { w, h };
    }
    const P = VIDEO_PROFILES[settings.profile] || VIDEO_PROFILES.common;
    return { w: P.maxWidth, h: P.maxHeight };
}

function recompute(it) {
    const s = screenSize();
    it.verdict = decideVideo(it.info, {
        profile: settings.profile,
        targetW: s.w, targetH: s.h,
        burnSubs: !!it.subFile,
    });
}

/* ───────────────────────────── добавление файлов ───────────────────────── */

async function addFiles(files) {
    const list = Array.from(files || []);
    const subs = list.filter((f) => SUB_EXT.indexOf(extOf(f.name)) >= 0);
    const vids = list.filter((f) => VIDEO_EXT.indexOf(extOf(f.name)) >= 0);

    if (subs.length && !vids.length) { attachSubs(subs); return; }
    if (!vids.length) { window.__toast && window.__toast(t('err_no_video'), 'warning'); return; }

    ui.stage.hidden = false;
    setBusy(true, t('busy_reading'));
    for (const f of vids) {
        const it = {
            id: 'v' + (++seq), file: f, name: f.name, ext: extOf(f.name), size: f.size,
            info: null, verdict: null, subFile: null, status: 'pending',
            limitKey: f.name + '|' + f.size + '|' + (f.lastModified || 0),
        };
        try {
            it.info = await analyzeVideo(f);
            recompute(it);
        } catch (e) { it.status = 'error'; it.error = String(e && e.message || e); }
        items.push(it);
        renderList();
        await new Promise((r) => setTimeout(r, 0));
    }
    setBusy(false);
    if (subs.length) attachSubs(subs);
    applyOrder();
    renderAll();
    stage('files_added', { n: items.length });
    ae('feature_use', { action: 'add_files', n: vids.length, total: items.length });
}

/**
 * Субтитры привязываются к ролику по ИМЕНИ, как это делают плееры: «film.srt»
 * к «film.mp4». Это не догадка ради удобства — так лежат субтитры у всех, и
 * ручной выбор «к какому ролику эти» на десяти файлах был бы работой, которую
 * человек уже проделал, назвав файлы.
 */
function attachSubs(subs) {
    let hit = 0;
    for (const s of subs) {
        const base = s.name.replace(/\.[^.]+$/, '').toLowerCase();
        const it = items.find((x) => x.name.replace(/\.[^.]+$/, '').toLowerCase() === base)
            || (items.length === 1 ? items[0] : null);
        if (it) { it.subFile = s; recompute(it); hit++; }
    }
    if (hit) {
        window.__toast && window.__toast(t('subs_attached', { n: hit }), 'success');
        ae('feature_use', { action: 'subs_attached', n: hit });
    } else window.__toast && window.__toast(t('subs_no_match'), 'warning');
    renderAll();
}

function applyOrder() {
    if (settings.order && settings.order !== 'as-is') {
        items = sortItems(items.map((it) => Object.assign(it, { tags: { title: it.name } })), settings.order);
    }
}

/* ─────────────────────────────── отрисовка ─────────────────────────────── */

const REASON_KEY = {
    container: 'why_container', codec: 'why_codec', profile: 'why_profile',
    profile_unknown: 'why_profile_unknown', resolution: 'why_resolution',
    fps: 'why_fps', audio_codec: 'why_audio', audio_channels: 'why_channels',
    burn_subs: 'why_subs',
};

function currentPlan() {
    const P = VIDEO_PROFILES[settings.profile] || VIDEO_PROFILES.common;
    return buildPlan(items.map((it) => ({
        id: it.id, tags: { title: it.name.replace(/\.[^.]+$/, '') },
        sourceName: it.name, ext: 'mp4', sizeBytes: it.size,
    })), {
        mode: settings.mode, numbering: settings.numbering, nameFrom: 'source',
        folders: settings.folders, maxPerFolder: 0, maxNameLen: 64,
    });
}

function renderList() {
    const plan = currentPlan();
    const byId = Object.create(null);
    for (const e of plan.entries) byId[e.id] = e;

    ui.tbody.innerHTML = items.map((it, i) => {
        const v = (it.info && it.info.video) || {};
        const e = byId[it.id];
        const meta = [];
        meta.push((it.info && it.info.container ? it.info.container.toUpperCase() : it.ext.toUpperCase()));
        if (v.family && v.family !== 'unknown') {
            meta.push(v.family.toUpperCase() + (v.profile ? ' ' + v.profile : ''));
        }
        if (v.width) meta.push(v.width + '×' + v.height);
        if (v.fps) meta.push(Math.round(v.fps) + ' fps');
        if (it.info && it.info.audio) meta.push(it.info.audio.family.toUpperCase()
            + (it.info.audio.channels > 2 ? ' ' + it.info.audio.channels + '.0' : ''));
        if (it.info && it.info.durationSec) meta.push(fmtTime(it.info.durationSec));
        meta.push(fmtSize(it.size));

        const badges = [];
        if (it.verdict) {
            for (const r of it.verdict.reasons) badges.push(`<span class="cv-badge cv-badge-re">${t(REASON_KEY[r] || r)}</span>`);
            if (it.verdict.splitParts) badges.push(`<span class="cv-badge cv-badge-fix">${t('fix_split', { n: it.verdict.splitParts })}</span>`);
        }
        if (!badges.length) badges.push(`<span class="cv-badge cv-badge-ok">${t('badge_ok')}</span>`);
        if (it.subFile) badges.push(`<span class="cv-badge cv-badge-fix">${t('badge_subs')}</span>`);

        const lane = it.verdict ? it.verdict.lane : 'copy';
        const out = it.verdict && lane === 'convert'
            ? `${it.verdict.outW}×${it.verdict.outH} · ${it.verdict.h264Profile}`
            : t('lane_copy_short');

        return `<tr data-id="${it.id}" draggable="true" class="cv-row${it.status === 'done' ? ' is-done' : ''}">
            <td class="cv-num">${i + 1}</td>
            <td><div class="cv-name" title="${esc(it.name)}">${esc(it.name)}</div>
                <div class="cv-meta">${meta.join(' · ')}</div></td>
            <td><div class="cv-out">${esc(e ? e.path : '—')}</div>
                <div class="cv-flags">${badges.join(' ')}</div></td>
            <td class="cv-lane"><span class="cv-lane-${lane}">${t(lane === 'convert' ? 'lane_convert' : 'lane_copy')}</span>
                <div class="cv-meta">${out}</div></td>
            <td class="cv-act"><button type="button" class="cv-x" data-del="${it.id}" aria-label="${t('remove')}">×</button></td>
        </tr>`;
    }).join('');
    ui.count.textContent = items.length;
}

function renderSummary() {
    const total = items.reduce((s, it) => s + it.size, 0);
    const conv = items.filter((it) => it.verdict && it.verdict.lane === 'convert').length;
    const dur = items.reduce((s, it) => s + ((it.info && it.info.durationSec) || 0), 0);

    const bits = [t('sum_files', { n: items.length })];
    if (dur) bits.push(t('sum_duration', { d: fmtTime(dur) }));
    bits.push(fmtSize(total));
    if (conv) bits.push(t('sum_convert', { n: conv }));
    ui.summary.textContent = bits.join(' · ');

    const warns = [];
    if (conv) {
        const secs = items.filter((it) => it.verdict && it.verdict.lane === 'convert')
            .reduce((s, it) => s + ((it.info && it.info.durationSec) || 0), 0);
        if (secs > 900) warns.push(t('warn_long_convert', { d: fmtTime(secs) }));
    }
    if (items.some((it) => it.subFile)) warns.push(t('warn_subs_slow'));
    if (items.some((it) => it.size > FAT32_MAX_FILE)) warns.push(t('warn_fat32'));
    ui.warn.innerHTML = warns.map((w) => `<div class="cv-warn">⚠️ ${w}</div>`).join('');

    ui.run.disabled = !items.length || running;
    ui.stage.hidden = !items.length;
}

const renderAll = () => { renderList(); renderSummary(); };

function setBusy(on, text) {
    ui.busy.hidden = !on;
    if (text) ui.busyText.textContent = text;
    ui.run.disabled = on || !items.length;
}
function setProgress(done, total, text) {
    ui.progWrap.hidden = false;
    ui.progBar.style.width = (total ? Math.round(done / total * 100) : 0) + '%';
    ui.progText.textContent = text || '';
}

/* ───────────────────────────── производство ────────────────────────────── */

let ffmpegReady = null;
async function ensureFfmpeg() {
    // Заглушка `__sharedModule` из layout существует ВСЕГДА и отвергает вызовы
    // через 15 с; признак настоящего модуля — отсутствие `__isStub`.
    const ready = () => (window.FFmpegProcessor && !window.FFmpegProcessor.__isStub);
    if (ready()) return window.FFmpegProcessor;
    if (!ffmpegReady) {
        ffmpegReady = new Promise((res, rej) => {
            const s = document.createElement('script');
            s.src = ui.cfg.ffmpegSrc; s.onload = res; s.onerror = rej;
            document.head.appendChild(s);
        });
    }
    await ffmpegReady;
    for (let i = 0; i < 100 && !ready(); i++) await new Promise((r) => setTimeout(r, 50));
    if (!ready()) throw new Error('ffmpeg module did not register');
    return window.FFmpegProcessor;
}

const PROFILE_ARG = { baseline: ['-profile:v', 'baseline', '-level', '3.0'],
    main: ['-profile:v', 'main', '-level', '3.1'],
    high: ['-profile:v', 'high', '-level', '4.0'] };

/** Перекодирование через FFmpeg — путь для нестандартных контейнеров и субтитров. */
async function convertWithFfmpeg(it, onProgress) {
    const F = await ensureFfmpeg();
    const V = it.verdict;
    const fps = Math.min((it.info.video && it.info.video.fps) || 30,
        (VIDEO_PROFILES[settings.profile] || VIDEO_PROFILES.common).maxFps);
    const br = pickBitrate(V.outW, V.outH, fps, V.h264Profile);

    const vf = [`scale=${V.outW}:${V.outH}:flags=bicubic`];
    if (it.subFile) {
        const srt = await prepareSubs(it);
        if (srt) {
            await F.writeFile('subs.srt', new TextEncoder().encode(srt));
            /* Прожиг идёт фильтром `subtitles`, который внутри FFmpeg рисует
               через libass. Своего сериализатора в ASS писать не надо: кегль,
               обводка и отступ задаются force_style, а всё остальное libass
               берёт из SRT сам.
               Кегль — от ВЫСОТЫ кадра: на 800×480 и на 1280×720 одинаковый
               кегль в пикселях читается по-разному, а расстояние до экрана в
               машине одно и то же. */
            const fs = Math.max(16, Math.round(V.outH * 0.075));
            const style = `FontSize=${fs},Outline=2,Shadow=1,MarginV=${Math.round(V.outH * 0.05)}`;
            vf.push(`subtitles=subs.srt:force_style='${style}'`);
        }
    }

    const args = [
        '-map', '0:v:0?', '-map', '0:a:0?',
        '-vf', vf.join(','),
        '-r', String(Math.round(fps)),
        '-c:v', 'libx264', '-preset', settings.quality === 'fast' ? 'ultrafast' : 'veryfast',
        ...PROFILE_ARG[V.h264Profile],
        '-pix_fmt', 'yuv420p',              // 4:2:0 — другого не декодирует ни одно устройство
        '-b:v', String(br), '-maxrate', String(Math.round(br * 1.4)), '-bufsize', String(br * 2),
        '-c:a', 'aac', '-b:a', '128k', '-ac', '2', '-ar', '44100',
        '-movflags', '+faststart',
    ];
    if (onProgress) F.onProgress(onProgress);
    const res = await F.process(it.file, args, 'car.mp4');
    return res.blob;
}

/**
 * Субтитры → UTF-8 SRT, который поймёт libass.
 *
 * ⚠️ Отдать FFmpeg исходный файл напрямую нельзя: русский .srt почти всегда в
 * windows-1251, и libass прочитает его кракозябрами — то есть прожжёт их в
 * картинку НАВСЕГДА. Общий разборщик проекта кодировку определяет (BOM,
 * UTF-16, строгий UTF-8, плотность кириллицы, cp1252), поэтому текст сюда
 * приходит уже правильным, а мы лишь пересобираем его в UTF-8.
 */
async function prepareSubs(it) {
    try {
        if (!window.SubtitleParser || !window.SubtitleSerializer) return null;
        const text = await window.SubtitleParser.readFile(it.subFile);
        const cues = window.SubtitleParser.parse(text, { preserveMarkup: false });
        if (!cues || !cues.length) return null;
        return window.SubtitleSerializer.toSrt(cues);
    } catch (e) { return null; }
}

/** Быстрый путь: аппаратный декод → энкод. */
async function convertWithWebCodecs(it, onProgress) {
    const V = it.verdict;
    const pipe = window.WebCodecsPipeline;
    if (!pipe) return null;
    const inCodec = (it.info.video && it.info.video.codecString) || 'avc1.640028';
    const cap = await pipe.isSupported({ inputCodec: inCodec, outputCodec: 'avc1.42e01e' });
    if (!cap || !cap.ok) return null;

    const fps = Math.min((it.info.video && it.info.video.fps) || 30,
        (VIDEO_PROFILES[settings.profile] || VIDEO_PROFILES.common).maxFps);
    /* ⚠️ `params.resolution` у общего пайплайна — это СТРОКА с числом
       КОРОТКОЙ стороны ('720', '1080p', '4k'), а не пара сторон. Объект
       {width,height} он молча игнорирует: `parseInt(object)` даёт NaN, ветка
       масштабирования не срабатывает, и файл выходит в исходном размере —
       ровно то, из-за чего он и не откроется на экране. Поймано прогоном:
       1920×1080 просили вписать в 1280×720, получили 1920×1080.
       Короткая сторона годится для обеих ориентаций: у горизонтального кадра
       это высота, у вертикального — ширина, а вписывание в экран уже посчитано
       в decideVideo. */
    const shortSide = Math.min(V.outW, V.outH);
    const inShort = Math.min(it.info.video.width || 0, it.info.video.height || 0);
    const res = await pipe.run({
        file: it.file,
        params: { resolution: (inShort && shortSide >= inShort) ? 'original' : String(shortSide) },
        videoBitrate: pickBitrate(V.outW, V.outH, fps, V.h264Profile),
        h264Profile: V.h264Profile,
        speed: settings.quality === 'fast' ? 'fast' : 'balanced',
        onProgress,
    });
    return res && res.blob ? res.blob : null;
}

/**
 * Проверить, что вышло то, что просили.
 *
 * Кодек-строка для VideoEncoder — ПОДСКАЗКА, а не требование: аппаратный
 * энкодер вправе выдать High там, где просили Baseline, и никакой ошибки при
 * этом не будет. Проверка стоит десятки миллисекунд (разбор заголовка), а её
 * отсутствие стоит человеку поездки с чёрным экраном.
 */
async function profileOf(blob) {
    try {
        const f = new File([blob], 'out.mp4', { type: 'video/mp4' });
        const info = await analyzeVideo(f);
        return (info.video && info.video.profile) || null;
    } catch (e) { return null; }
}

async function produce(it, onProgress) {
    const V = it.verdict;
    if (V.lane === 'copy') return it.file;

    const mp4Family = it.info.container === 'mp4';
    let blob = null;

    // Быстрый путь возможен только для mp4-семейства и без прожига субтитров:
    // libass живёт в FFmpeg, а не в WebCodecs.
    if (mp4Family && !it.subFile) {
        try { blob = await convertWithWebCodecs(it, onProgress); }
        catch (e) { blob = null; }
        if (blob) {
            const got = await profileOf(blob);
            const want = V.h264Profile;
            if (got && PROFILE_RANK[got] > PROFILE_RANK[want]) {
                // Энкодер проигнорировал просьбу. Молча отдать такой файл
                // нельзя: он «удался» и не откроется в машине.
                ae('feature_use', { action: 'profile_rejected', want, got });
                it.profileFallback = { want, got };
                blob = null;
            } else {
                it.laneUsed = 'webcodecs';
            }
        }
    }

    if (!blob) {
        blob = await convertWithFfmpeg(it, onProgress);
        it.laneUsed = 'ffmpeg';
    }
    return blob;
}

/* ─────────────────────────── разбивка по 4 ГиБ ─────────────────────────── */

/**
 * Файл больше 4 ГиБ на FAT32 не ложится. Резать перекодированием было бы
 * дважды неправильно (время и качество) — режем потоковым копированием.
 */
async function splitToParts(blob, durationSec, name) {
    const parts = Math.ceil(blob.size / (FAT32_MAX_FILE * 0.95));
    if (parts <= 1) return [{ blob, suffix: '' }];
    const F = await ensureFfmpeg();
    const chunk = durationSec / parts;
    const file = new File([blob], name, { type: 'video/mp4' });
    const out = [];
    for (let i = 0; i < parts; i++) {
        const res = await F.process(file, [
            '-ss', String(Math.round(i * chunk)),
            '-t', String(Math.ceil(chunk) + 1),
            '-c', 'copy', '-avoid_negative_ts', 'make_zero', '-movflags', '+faststart',
        ], 'part.mp4');
        out.push({ blob: res.blob, suffix: ' part' + String(i + 1).padStart(2, '0') });
    }
    return out;
}

/* ────────────────────────────────── запуск ─────────────────────────────── */

async function run() {
    if (running || !items.length) return;

    let limit = null;
    if (window.__toolLimit) {
        // Единица — подготовленный носитель целиком (составной ключ), как и у
        // музыкальной половины: пофайловая единица закрыла бы тул на втором
        // ролике, а смысл в том, чтобы собрать флешку.
        const key = 'set:' + items.map((it) => it.limitKey).sort().join('|');
        limit = window.__toolLimit.begin(SLUG, [key]);
        if (!limit) return;
    }

    running = true;
    ui.run.disabled = true;
    ui.report.hidden = true;
    const plan = currentPlan();
    const byId = Object.create(null);
    for (const e of plan.entries) byId[e.id] = e;

    const t0 = Date.now();
    let written = 0, failed = 0, converted = 0;
    let batch = null;

    try {
        batch = await window.__batchDeliver.openBatch({ zipName: 'car-video.zip', slug: SLUG, total: items.length });
        if (batch.mode !== 'folder') window.__toast && window.__toast(t('warn_zip'), 'warning');

        let i = 0;
        for (const it of items) {
            const e = byId[it.id];
            const base = e.base;
            setProgress(i, items.length, t('prog_file', { i: i + 1, n: items.length, name: it.name }));
            try {
                const blob = await produce(it, (p) => {
                    setProgress(i + (p || 0), items.length,
                        t('prog_file_pct', { i: i + 1, n: items.length, name: it.name, p: Math.round((p || 0) * 100) }));
                });
                const dur = (it.info && it.info.durationSec) || 0;
                const parts = blob.size > FAT32_MAX_FILE
                    ? await splitToParts(blob, dur, base + '.mp4')
                    : [{ blob, suffix: '' }];
                for (const part of parts) {
                    const path = (e.folder ? e.folder + '/' : '') + base + part.suffix + '.mp4';
                    await batch.write(path, part.blob);
                }
                it.status = 'done';
                written += parts.length;
                if (it.verdict.lane === 'convert') converted++;
            } catch (err) {
                it.status = 'error';
                it.error = String(err && err.message || err);
                failed++;
                ae('tool_error', { action: 'produce_failed', ext: it.ext, err: it.error.slice(0, 90) });
            }
            i++;
            await new Promise((r) => setTimeout(r, 0));
        }

        const res = await batch.finish();
        showReport({ written, failed, converted, via: (res && res.via) || batch.mode, ms: Date.now() - t0 });
        if (limit && written) limit.commit(); else if (limit) limit.abort();
        stage('written', { n: written, via: batch.mode });
        ae('tool_complete', {
            n: items.length, written, failed, converted,
            profile: settings.profile, via: batch.mode, ms: Date.now() - t0,
            lanes: items.map((x) => x.laneUsed || 'copy').join(','),
        });
    } catch (err) {
        if (limit) limit.abort();
        if (batch && batch.abort) try { batch.abort(); } catch (e) {}
        window.__handleError ? window.__handleError(err, SLUG) : (window.__toast && window.__toast(String(err), 'error'));
        ae('tool_error', { action: 'run_failed', err: String(err && err.message || err).slice(0, 120) });
    } finally {
        running = false;
        ui.progWrap.hidden = true;
        renderAll();
    }
}

function showReport(r) {
    ui.report.hidden = false;
    const facts = [t('rep_copied', { n: r.written - r.converted }), t('rep_seconds', { s: (r.ms / 1000).toFixed(0) })];
    if (r.converted) facts.unshift(t('rep_converted', { n: r.converted }));
    if (r.failed) facts.push(t('rep_failed', { n: r.failed }));
    const fb = items.filter((x) => x.profileFallback);
    const lines = [
        `<div class="cv-rep-title">✅ ${t('rep_title', { n: r.written })}</div>`,
        `<div class="cv-rep-facts">${facts.join(' · ')}</div>`,
        `<div class="cv-rep-next">${t(r.via === 'folder' ? 'rep_next_folder' : 'rep_next_zip')}</div>`,
    ];
    if (fb.length) {
        lines.push(`<div class="cv-warn">ℹ️ ${t('rep_profile_fallback', { n: fb.length, got: fb[0].profileFallback.got })}</div>`);
    }
    ui.report.innerHTML = lines.join('');
}

/* ──────────────────────────────── перетаскивание ───────────────────────── */

function wireDrag() {
    let dragId = null;
    ui.tbody.addEventListener('dragstart', (e) => {
        const row = e.target.closest('tr[data-id]');
        if (!row) return;
        dragId = row.dataset.id; row.classList.add('is-drag');
        try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', dragId); } catch (_) {}
    });
    ui.tbody.addEventListener('dragend', () => {
        dragId = null;
        ui.tbody.querySelectorAll('.is-drag,.is-over').forEach((r) => r.classList.remove('is-drag', 'is-over'));
    });
    ui.tbody.addEventListener('dragover', (e) => {
        const row = e.target.closest('tr[data-id]');
        if (!row || !dragId) return;
        e.preventDefault();
        ui.tbody.querySelectorAll('.is-over').forEach((r) => r.classList.remove('is-over'));
        row.classList.add('is-over');
    });
    ui.tbody.addEventListener('drop', (e) => {
        const row = e.target.closest('tr[data-id]');
        if (!row || !dragId) return;
        e.preventDefault();
        const from = items.findIndex((x) => x.id === dragId);
        const to = items.findIndex((x) => x.id === row.dataset.id);
        if (from < 0 || to < 0 || from === to) return;
        const [m] = items.splice(from, 1);
        items.splice(to, 0, m);
        settings.order = 'as-is';
        if ($('cvOrder')) $('cvOrder').value = 'as-is';
        renderAll();
    });
}

/* ──────────────────────────────────── монтаж ───────────────────────────── */

export function mount(cfg) {
    ui = {
        cfg, lang: cfg.lang || {},
        stage: $('cvStage'), tbody: $('cvBody'), count: $('cvCount'),
        summary: $('cvSummary'), warn: $('cvWarn'), run: $('cvRun'),
        busy: $('cvBusy'), busyText: $('cvBusyText'),
        progWrap: $('cvProgWrap'), progBar: $('cvProgBar'), progText: $('cvProgText'),
        report: $('cvReport'),
    };

    const bind = (id, key, def, kind) => {
        const el = $(id);
        if (!el) return;
        if (window.__toolSettings) window.__toolSettings.bind(SLUG, el, key, def);
        settings[key] = kind === 'bool' ? !!el.checked : (el.value || def);
        el.addEventListener('change', () => {
            settings[key] = kind === 'bool' ? el.checked : el.value;
            if (key === 'order') applyOrder();
            if (key === 'profile') syncScreens();
            items.forEach(recompute);
            renderAll();
        });
    };
    bind('cvProfile', 'profile', 'common');
    bind('cvScreen', 'screen', 'auto');
    bind('cvMode', 'mode', 'ascii');
    bind('cvOrder', 'order', 'as-is');
    bind('cvFolders', 'folders', 'flat');
    bind('cvQuality', 'quality', 'balanced');
    bind('cvNumbering', 'numbering', true, 'bool');

    /* Через `__bindUploaderFile`, а НЕ голым addEventListener: голая подписка
       гонится с примитивом — если файл выбрали раньше, чем модуль успел
       подписаться, событие уже улетело и тул стоит мёртвый. Примитив
       чинит это ретроактивным повтором и снятием дублей.
       ⚠️ Колбэк зовётся как (File, detail) — НЕ событием; множественный
       выбор живёт в detail.files. */
    const zone = $('cvUpload');
    if (zone) window.__bindUploaderFile(zone, (file, detail) => {
        const d = detail || {};
        addFiles(d.files && d.files.length ? d.files : (file ? [file] : []));
    });

    const on = (id, fn) => { const el = $(id); if (el) el.addEventListener('click', fn); };
    on('cvRun', run);
    on('cvClear', () => { items = []; renderAll(); ui.report.hidden = true; });

    ui.tbody.addEventListener('click', (e) => {
        const id = e.target && e.target.dataset && e.target.dataset.del;
        if (!id) return;
        items = items.filter((x) => x.id !== id);
        renderAll();
    });

    wireDrag();
    syncScreens();
    renderAll();
}

/** Список экранов зависит от поколения — у Android-магнитолы нет 800×480. */
function syncScreens() {
    const sel = $('cvScreen');
    if (!sel) return;
    const P = VIDEO_PROFILES[settings.profile] || VIDEO_PROFILES.common;
    const keep = sel.value;
    sel.innerHTML = `<option value="auto">${t('screen_auto', { w: P.maxWidth, h: P.maxHeight })}</option>`
        + P.screens.map(([w, h]) => `<option value="${w}x${h}">${w} × ${h}</option>`).join('');
    if ([...sel.options].some((o) => o.value === keep)) sel.value = keep;
    settings.screen = sel.value;
}

function boot() {
    const cfg = window.__CAR_VIDEO_CFG;
    if (!cfg || !document.getElementById('cvUpload')) return;
    try { mount(cfg); } catch (e) {
        window.__handleError ? window.__handleError(e, SLUG) : console.error(e);
    }
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
